import Vue from "vue";
import {RawLocation, Location, Route} from "vue-router";
import {safeMeta, PositivePermissionType, PermissionType} from "@/router/Meta";
import {isString} from "@/utils/TypeUtil";

/**
 * 行き先
 */
export type DestinationType =
  'edit' | // 編集モード
  'browse'; // 閲覧モード

/**
 * パーミッションプロバイダー
 */
export interface PermissionProvider extends Vue {
  /**
   * 画面遷移が許されるかどうかを取得します
   *
   * @param destination 相対的な行き先
   * @param route 行き先ルート
   * @return 許される場合 true そうでない場合 false 祖先コンポーネントに委ねる場合 undefined
   */
  isTransitionPermitted?: (destination: DestinationType, route: Route) => boolean | undefined;
}

/**
 * 指定された権限があるかどうかを返します
 *
 * @param vue 権限を検証する Vue コンポーネント
 * @param permissions 検証したい権限の配列
 */
function hasPermissionsImpl(vue: Vue, permissions: PermissionType[]): boolean {
  // パーミッションが指定されていない場合
  if(!permissions.length) {
    return true;
  }

  // 未ログイン状態の場合
  const account = vue.$store.getters['hcs/account'];
  if (!account.user || !account.owner) {
    return false;
  }

  // ユーザーの保持しているパーミッションを列挙
  const userPermissions = [] as PositivePermissionType[];
  if(account.user.permissionAdmin) userPermissions.push('admin');
  if(account.user.permissionContract) userPermissions.push('contract');
  if(account.user.permissionImplementation) userPermissions.push('implementation');
  if(account.user.permissionDictionary) userPermissions.push('dictionary');
  if(account.owner.platformOwner) userPermissions.push('platformOwner');
  if(account.owner.componentOwner) userPermissions.push('componentOwner');

  // ページに必要なパーミッションがユーザーにあるかどうか
  let negative: 1 | boolean = 1,
    positive: 1 | boolean = 1;
  for(let permission of permissions) {
    if(permission.charAt(0) === '!') {
      permission = permission.substr(1) as PositivePermissionType;
      negative = negative && !userPermissions.includes(permission);
    } else {
      positive = positive && userPermissions.includes(permission as PositivePermissionType);
    }
  }
  return !((negative === 1 || !negative) && (positive === 1 || !positive));
}

/**
 * 指定されたページを表示する権限があるかどうかを返します
 *
 * @param vue 権限を検証する Vue コンポーネント
 * @param route 検証したいページのルート
 * @param destination 相対的な行き先
 */
function hasPagePermissionImpl(vue: Vue, route: Route, destination?: DestinationType): boolean {
  const pagePermissions = safeMeta(route).permissions || [];

  if(!hasPermissionsImpl(vue, pagePermissions)) {
    return false;
  }

  // PermissionProvider に委譲
  if(destination) {
    let vm = vue as PermissionProvider;
    do {
      const isTransitionPermittedFn = vm.isTransitionPermitted;
      if (typeof isTransitionPermittedFn === 'function') {
        const result = isTransitionPermittedFn(destination, route);
        if (result !== undefined) {
          return result;
        }
      }
      vm = vm.$parent as PermissionProvider;
    } while (vm);
  }

  return true;
}

/**
 * フォールバック情報
 */
interface Fallback {
  /**
   * 与えられたロケーションから何回フォールバックしたかの数
   * 与えられたロケーションと等価の場合は 0
   */
  hop: number;

  /**
   * 有効なロケーション
   */
  location: Location;

  /**
   * 有効なルート
   */
  route: Route;

  /**
   * 始点となるルート
   */
  initialRoute: Route;
}

/**
 * パーミッションについてのユーティリティミックスイン
 */
export default Vue.extend({
  methods: {
    /**
     * 指定された権限があるかどうかを返します
     *
     * @param permissions 検証したい権限の配列
     */
    hasPermissions(permissions: PermissionType[]): boolean {
      return hasPermissionsImpl(this, permissions);
    },

    /**
     * 指定されたページを表示する権限があるかどうかを返します
     *
     * @param page 検証したいページのロケーション
     * @param destination 相対的な行き先
     */
    hasPagePermission(page: RawLocation, destination?: DestinationType): boolean {
      const {route} = this.$router.resolve(page);
      return hasPagePermissionImpl(this, route, destination);
    },

    /**
     * フォールバックページが存在するかどうかを取得します。
     * フォールバックページが存在する場合、そのページのロケーションを返します。
     *
     * @param page 検証したいページのロケーション
     * @param destination 相対的な行き先
     * @return フォールバックページが存在する場合・フォールバックする必要が無かった場合はフォールバック情報、そうでない場合 false
     */
    hasFallbackPage(page: RawLocation, destination?: DestinationType): false | Fallback {
      const dejavu = new Set();
      let resolved = this.$router.resolve(page);
      const initialRoute = resolved.route;
      do {
        if(dejavu.has(resolved.route.name)) {
          return false; // フォールバックがループした場合
        } else {
          dejavu.add(resolved.route.name);
        }
        if (hasPagePermissionImpl(this, resolved.route, destination)) {
          return {
            hop: dejavu.size - 1,
            location: resolved.location,
            route: resolved.route,
            initialRoute
          };
        }
        let fallbackLocation = resolved.route.meta?.fallback;
        if (!fallbackLocation) {
          return false;
        }
        if(isString(fallbackLocation)) {
          fallbackLocation = {name: fallbackLocation};
        }
        resolved = this.$router.resolve(fallbackLocation);
      } while(resolved);
      return false;
    }
  }
});
